/**
 * **Qué días pide una solicitud de vacaciones**, para escribirlo en pantalla.
 *
 * Una solicitud puede llevar **varios tramos sueltos** —el formulario deja añadirlos y los manda
 * así—, y `fechaInicio`/`fechaFin` son solo los dos extremos: pedir del 3 al 5 y del 20 al 22 sale
 * como «3 – 22 dic» si se leen esos dos campos. En la bandeja del aprobador eso no es un detalle
 * de formato: la fila y la confirmación de aprobar afirmaban un periodo que nadie ha pedido, con
 * veinte días de diferencia, y quien decide no podía ver qué intervalos estaba resolviendo
 * (revisión CODEX del PR #1735, ronda 8).
 *
 * Está aquí y no en cada pantalla porque lo escriben **tres**: la bandeja, el detalle del día
 * amarillo del calendario y «Mis solicitudes». Las tres tenían su propia copia de los dos extremos
 * y habría que acordarse de las tres.
 *
 * El formato del día lo pone quien llama (`fechaCorta`): la bandeja escribe «21 dic» y el
 * calendario «21/12», y ninguna de las dos pasa el texto ISO por `Date` —un `LocalDate` llega como
 * «2026-12-21» y parsearlo lo movería un día según la zona del navegador—.
 */

/**
 * Los tramos de la solicitud, con los dos extremos siempre puestos.
 *
 * Sin `tramos` se cae a `fechaInicio`–`fechaFin`: las solicitudes de un solo tramo y las que
 * vengan de datos anteriores al campo se siguen escribiendo igual que antes.
 *
 * @param solicitud la solicitud tal como la devuelve el servidor
 * @returns lista de `{desde, hasta}`; vacía si no hay fechas que escribir
 */
export function tramosDe(solicitud) {
  if (!solicitud) return [];
  const tramos = [];
  (solicitud.tramos || []).forEach(function(tramo) {
    if (!tramo || (!tramo.desde && !tramo.hasta)) return;
    tramos.push({ desde: tramo.desde || tramo.hasta, hasta: tramo.hasta || tramo.desde });
  });
  if (tramos.length) return tramos;
  if (!solicitud.fechaInicio && !solicitud.fechaFin) return [];
  return [{
    desde: solicitud.fechaInicio || solicitud.fechaFin,
    hasta: solicitud.fechaFin || solicitud.fechaInicio
  }];
}

/**
 * «21 dic – 30 dic», «21 dic» cuando empieza y acaba el mismo día, y «3 – 5 dic · 20 – 22 dic»
 * cuando son varios tramos.
 *
 * @param solicitud la solicitud
 * @param fechaCorta cómo escribe un día quien llama
 * @returns el texto, o '' si no hay fechas
 */
export default function fechasDeLaSolicitud(solicitud, fechaCorta) {
  const textos = [];
  tramosDe(solicitud).forEach(function(tramo) {
    const desde = fechaCorta(tramo.desde);
    const hasta = fechaCorta(tramo.hasta);
    const texto = desde === hasta ? desde : desde + ' – ' + hasta;
    if (texto) textos.push(texto);
  });
  return textos.join(' · ');
}
